import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, MapPin, Building2, Phone, Mail, ExternalLink, Sparkles, Info } from "lucide-react";
import { motion } from "framer-motion";
import { getClinics } from "../lib/api";
import type { Clinic } from "../lib/types";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";

interface ClinicsIndexProps {
  onNavigate?: (page: string, clinicId?: string) => void;
}

// Fisher-Yates shuffle algorithm for randomization
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export function ClinicsIndex({ onNavigate }: ClinicsIndexProps) {
  const { profile } = useAuth();
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCity, setFilterCity] = useState("all");
  const [filterState, setFilterState] = useState("all");

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        setLoading(true);
        const data = await getClinics({ activeOnly: true });
        
        // Randomise clinics for verified patients on each page load
        const finalData = profile?.is_verified_patient ? shuffleArray(data) : data;
        setClinics(finalData);
        
        if (profile?.is_verified_patient) {
          console.log('🎲 Clinics randomised for verified patient');
        }
      } catch (error: any) {
        console.error("Error fetching clinics:", error);
        toast.error("Failed to load clinics");
      } finally {
        setLoading(false);
      }
    };

    fetchClinics();
  }, [profile?.is_verified_patient]);

  // Get unique cities and states for filters
  const cities = [...new Set(clinics.map(c => c.city).filter(Boolean))].sort() as string[];
  const states = [...new Set(clinics.map(c => c.state).filter(Boolean))].sort() as string[];

  const filteredClinics = clinics.filter(clinic => {
    if (filterCity !== "all" && clinic.city !== filterCity) return false;
    if (filterState !== "all" && clinic.state !== filterState) return false;
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        clinic.name.toLowerCase().includes(search) ||
        clinic.city?.toLowerCase().includes(search) ||
        clinic.address?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="text-4xl sm:text-5xl mb-3">Medical Cannabis Clinics</h1>
          <p className="text-xl text-muted-foreground mb-6">
            Independent, neutral directory of UK medical cannabis clinics. We don't name or price any medicines and we don't provide medical advice.
          </p>
          
          <Alert className="bg-primary/5 border-primary/20 max-w-2xl">
            <Info className="text-primary" />
            <AlertDescription className="text-foreground">
              <strong className="text-primary">Non-promotion:</strong> CannaBias is service-only. We do not advertise prescription-only medicines to the public.
            </AlertDescription>
          </Alert>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-8"
        >
          <Card className="p-6 glass-card">
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    placeholder="Search clinics by name, city, or address..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-12 bg-background/50 border-border/50 focus:bg-background transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Select value={filterCity} onValueChange={setFilterCity}>
                  <SelectTrigger className="w-[200px]">
                    <MapPin className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterState} onValueChange={setFilterState}>
                  <SelectTrigger className="w-[200px]">
                    <MapPin className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {states.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(searchQuery || filterCity !== "all" || filterState !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setFilterCity("all");
                      setFilterState("all");
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-6 w-3/4 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3 mb-4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Clinics Grid */}
        {!loading && (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredClinics.map((clinic) => (
              <motion.div key={clinic.id} variants={item}>
                <Card
                  className="p-6 glass-card hover-lift cursor-pointer group h-full card-shine"
                  onClick={() => onNavigate && onNavigate("clinic-detail", clinic.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    {clinic.is_active && (
                      <Badge className="gradient-green text-white">
                        Active
                      </Badge>
                    )}
                  </div>

                  <h3 className="text-lg mb-3 group-hover:text-primary transition-colors line-clamp-2">
                    {clinic.name}
                  </h3>

                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    {clinic.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">
                          {clinic.address}
                          {clinic.city && `, ${clinic.city}`}
                          {clinic.county && `, ${clinic.county}`}
                          {clinic.postcode && ` ${clinic.postcode}`}
                        </span>
                      </div>
                    )}

                    {clinic.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{clinic.phone}</span>
                      </div>
                    )}

                    {clinic.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{clinic.email}</span>
                      </div>
                    )}
                  </div>

                  {clinic.website && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(clinic.website!, "_blank");
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visit Website
                    </Button>
                  )}

                  {clinic.licence_number && (
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/50">
                      Licence: {clinic.licence_number}
                    </p>
                  )}
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && filteredClinics.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted/50 flex items-center justify-center">
              <Building2 className="w-12 h-12 text-muted-foreground" />
            </div>
            <h3 className="text-2xl mb-2">No clinics found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filters
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setFilterCity("all");
                setFilterState("all");
              }}
            >
              Clear all filters
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}